import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search } from "lucide-react";

export default function Subjects() {
  const [data, setData] = useState({ categories: [], subjects: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [level, setLevel] = useState("all");

  useEffect(() => { api.get("/subjects").then((r) => setData(r.data)); }, []);

  const filtered = useMemo(() => {
    return data.subjects.filter((s) => {
      if (cat !== "All" && s.category !== cat) return false;
      if (level !== "all" && s.level !== level) return false;
      if (q && !s.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data.subjects, q, cat, level]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of filtered) (g[s.category] ||= []).push(s);
    return g;
  }, [filtered]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 animate-fade-up">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[color:var(--bee-muted)] mb-2">Explore</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">The whole syllabus.</h1>
        <p className="text-[color:var(--bee-muted)] mt-3 max-w-2xl">Every high school and university subject has a forum, study room and call space. Pick yours.</p>
      </div>

      {/* Filters */}
      <div className="bee-card p-4 mb-8 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bee-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 100+ subjects..."
            data-testid="subject-search"
            className="bee-input w-full pl-9"
          />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="subject-category" className="bee-input md:w-56">
          <option value="All">All categories</option>
          {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="subject-level" className="bee-input md:w-40">
          <option value="all">All levels</option>
          <option value="highschool">High school</option>
          <option value="university">University</option>
        </select>
      </div>

      {Object.entries(grouped).map(([category, subs]) => (
        <section key={category} className="mb-12">
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="font-display text-2xl font-semibold">{category}</h2>
            <span className="text-xs text-[color:var(--bee-muted)]">{subs.length} subjects</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subs.map((s) => (
              <Link key={s.slug} to={`/subjects/${s.slug}`} data-testid={`subject-card-${s.slug}`} className="bee-card p-5 group">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 hex-clip bg-[color:var(--bee-primary)] group-hover:bg-[color:var(--bee-text)] group-hover:text-[color:var(--bee-primary)] flex items-center justify-center font-display font-bold transition-colors">
                    {s.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{s.name}</h3>
                      <span className="bee-chip text-[10px] tracking-wider uppercase">{s.level === "highschool" ? "HS" : "Uni"}</span>
                    </div>
                    <p className="text-xs text-[color:var(--bee-muted)] line-clamp-2 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[color:var(--bee-muted)]">No subjects match your filter.</div>
      )}
    </div>
  );
}
